import { LEGAL_DOCUMENTS, type LegalDocId } from '../content/legal'

export const SITE_URL = 'https://www.zimark.org'
export const SITE_NAME = 'Zimark'
export const SITE_LOCALE = 'en_US'
export const THEME_COLOR = '#2c417a'

export const DEFAULT_TITLE = 'Zimark — Learn Faster. Review Smarter.'
export const DEFAULT_DESCRIPTION =
  'Zimark is an AI-first homework workspace built around Lucas, your teaching assistant. Create course workspaces, upload syllabi and class files, and get step-by-step help tailored to your coursework. Free to start.'

export const DEFAULT_KEYWORDS = [
  'Zimark',
  'Zimark Education',
  'AI homework help',
  'course workspace',
  'Lucas AI tutor',
  'study assistant',
  'exam prep',
  'learning platform',
].join(', ')

export const OG_IMAGE_PATH = '/og-image.svg'
export const OG_IMAGE_URL = `${SITE_URL}${OG_IMAGE_PATH}`

export type PageSeo = {
  title: string
  description: string
  path: string
  robots?: string
  ogType?: string
}

const NOINDEX_EXACT = new Set(['/tutor', '/school', '/admin/login'])

const PRIVATE_ROUTE_PREFIXES = [
  '/admin',
  '/messages',
  '/workspace',
  '/course-materials',
  '/account-settings',
  '/my-tutor',
  '/my-payments',
  '/search-results',
  '/session-details',
  '/school',
  '/tutor/profile',
  '/instructor-dashboard',
  '/auth/',
]

export function isPrivateRoute(pathname: string): boolean {
  if (NOINDEX_EXACT.has(pathname)) return true
  if (pathname === '/tutor/register' || pathname === '/school/register') return false
  return PRIVATE_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

export function getSeoForPath(pathname: string): PageSeo {
  if (
    pathname === '/terms' ||
    pathname === '/privacy' ||
    pathname === '/cookies' ||
    pathname === '/ip-disclaimer'
  ) {
    const docId = pathname.slice(1) as LegalDocId
    const doc = LEGAL_DOCUMENTS[docId]
    return {
      title: `${doc.title} | ${SITE_NAME}`,
      description: `${doc.title} for ${doc.company}. Last updated ${doc.updatedLine.replace(/\s+/g, ' ').trim()}.`,
      path: pathname,
      ogType: 'article',
    }
  }

  if (pathname === '/updates' || pathname.startsWith('/updates/')) {
    return {
      title: 'Updates | Zimark',
      description: 'Product updates and release notes from Zimark Education.',
      path: pathname,
      ogType: pathname === '/updates' ? 'website' : 'article',
    }
  }

  if (pathname === '/blog' || pathname.startsWith('/blog/')) {
    return {
      title: 'Blog | Zimark',
      description: 'Notes on learning, Lucas, and building better study habits with Zimark.',
      path: pathname,
      ogType: pathname === '/blog' ? 'website' : 'article',
    }
  }

  if (isPrivateRoute(pathname)) {
    return {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      path: pathname,
      robots: 'noindex, nofollow',
    }
  }

  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    path: pathname === '/' ? '/' : pathname,
    ogType: 'website',
  }
}

export function absoluteUrl(path: string): string {
  if (path === '/') return `${SITE_URL}/`
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}
